<template>
  <Transition name="side-panel">
    <div
      v-if="isOpen"
      class="add-widget-panel-container"
      :class="{ 'embedded': embedded }"
      @click="embedded ? null : handleBackdropClick"
    >
      <div
        class="add-widget-panel"
        @click.stop
      >
        <div class="panel-header">
          <button
            class="close-btn"
            aria-label="Close panel"
            @click="close"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <h2>Add Widget</h2>
        </div>

        <div class="panel-body">
          <div
            class="widgets-content"
            :class="{ 'preview-mode-focused': targetSectionIsFocused }"
          >
            <div
              v-if="availableWidgets.length === 0"
              class="no-widgets"
            >
              <p>No widgets available.</p>
            </div>
            <template v-else>
              <div
                v-for="group in widgetsBySection"
                :key="group.section"
                class="widget-section"
              >
                <h3 class="widget-section-header">
                  {{ sentenceCase(group.section) }}
                </h3>
                <div class="widgets-grid">
                  <div
                    v-for="widget in group.widgets"
                    :key="widget.id"
                    :ref="(el) => setWidgetCardRef(el, widget.id)"
                    :data-widget-id="widget.id"
                    class="widget-card"
                    :class="{ 'widget-card--restricted': isRestricted(widget.id) }"
                    @mouseleave="resetAddedOnHoverAway(widget.id)"
                  >
                    <div
                      class="widget-preview"
                      :class="{ 'preview-large': !targetSectionIsFocused, 'preview-focused': targetSectionIsFocused }"
                    >
                      <div
                        v-if="!isRestricted(widget.id) && isWidgetOnDashboard(widget.id)"
                        class="widget-on-dashboard-badge"
                        @mouseenter="handleAddedBadgeMouseEnter($event, widget.id)"
                        @mouseleave="handleAddedBadgeMouseLeave"
                      >
                        <span class="widget-on-dashboard-badge-pill">Added</span>
                      </div>
                      <div
                        class="preview-container"
                        :class="{ 'preview-container--blurred': isRestricted(widget.id) }"
                      >
                        <div class="preview-wrapper">
                          <template v-if="isRestricted(widget.id) || !isWidgetVisible(widget.id)">
                            <div class="widget-fake-preview">
                              <div class="widget-fake-title">
                                {{ widget.name }}
                              </div>
                              <div class="widget-fake-content">
                                <div class="widget-fake-bar" />
                                <div class="widget-fake-bar widget-fake-bar--short" />
                                <div class="widget-fake-bar widget-fake-bar--medium" />
                              </div>
                            </div>
                          </template>
                          <component
                            :is="widget.component"
                            v-else
                            v-bind="previewPropsForWidget(widget)"
                            :edit-mode="false"
                            :focused="targetSectionIsFocused"
                          />
                        </div>
                      </div>
                      <!-- Hover overlay: Add button (only when targetSectionId is set - widgets added from within a section) -->
                      <div
                        v-if="targetSectionId"
                        class="widget-preview-overlay"
                        :class="{ 'widget-preview-overlay--restricted': isRestricted(widget.id) }"
                      >
                        <div class="widget-add-actions widget-add-actions-centered widget-add-actions--tooltip-trigger">
                          <button
                            type="button"
                            class="widget-add-btn"
                            :class="{ 'is-adding': isAdding(widget.id), 'is-added': isAdded(widget.id), 'is-restricted': isRestricted(widget.id) }"
                            :disabled="isAdding(widget.id) || isAdded(widget.id) || isRestricted(widget.id)"
                            @click.stop="!isRestricted(widget.id) && addWidgetToSection(targetSectionId, widget)"
                          >
                            <span
                              v-if="isAdding(widget.id)"
                              class="add-btn-loading"
                            >
                              <span class="dot" /><span class="dot" /><span class="dot" />
                            </span>
                            <span v-else-if="isAdded(widget.id)">Added</span>
                            <span v-else-if="isRestricted(widget.id)">Add</span>
                            <span v-else>Add</span>
                          </button>
                          <span
                            v-if="isRestricted(widget.id)"
                            class="role-restricted-tooltip"
                          >Not available for your role</span>
                        </div>
                      </div>
                      <div
                        v-else
                        class="widget-preview-overlay widget-preview-overlay-disabled"
                      >
                        <div class="widget-add-actions widget-add-actions-centered">
                          <span class="widget-add-disabled-message">{{ isRestricted(widget.id) ? 'Not available for your role' : 'Add a section first' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Tooltip teleported to body to escape overflow: hidden -->
  <Teleport to="body">
    <div
      v-if="hoveredAddedBadgeWidgetId && hoveredAddedBadgeEl"
      ref="addedBadgeTooltipRef"
      class="widget-on-dashboard-tooltip widget-on-dashboard-tooltip--teleported"
      :style="{ left: addedBadgeTooltipStyle.left, top: addedBadgeTooltipStyle.top, transform: addedBadgeTooltipStyle.transform }"
    >
      {{ getAddedTooltip(hoveredAddedBadgeWidgetId) }}
    </div>
  </Teleport>
</template>

<script setup>
import { markRaw, computed, ref, watch, onMounted, onBeforeUnmount, nextTick, provide } from 'vue'

// Tell child widget components to limit data fetching (e.g. fetch 3 items instead of 500)
provide('widgetPreviewMode', true)

// --- Lazy-mount: only render widget components once their card scrolls into view ---
const visibleWidgetIds = ref(new Set())
const widgetCardRefs = new Map() // widgetId -> DOM element
let observer = null

function setWidgetCardRef(el, widgetId) {
  if (el) {
    widgetCardRefs.set(widgetId, el)
    if (observer) observer.observe(el)
  } else {
    widgetCardRefs.delete(widgetId)
  }
}

function createObserver() {
  if (observer) return
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const widgetId = entry.target.dataset.widgetId
          if (widgetId && !visibleWidgetIds.value.has(widgetId)) {
            visibleWidgetIds.value = new Set([...visibleWidgetIds.value, widgetId])
          }
          // Once visible, stop observing (keep mounted)
          observer.unobserve(entry.target)
        }
      }
    },
    { rootMargin: '200px 0px' } // pre-load slightly before scrolling into view
  )
  // Observe any refs already registered
  for (const [, el] of widgetCardRefs) {
    observer.observe(el)
  }
}

function destroyObserver() {
  if (observer) {
    observer.disconnect()
    observer = null
  }
  widgetCardRefs.clear()
  visibleWidgetIds.value = new Set()
}

onBeforeUnmount(destroyObserver)

const isWidgetVisible = (widgetId) => visibleWidgetIds.value.has(widgetId)

const addingWidgetId = ref(null)
const hoveredAddedBadgeEl = ref(null)
const hoveredAddedBadgeWidgetId = ref(null)
const addedWidgetIds = ref(new Set())

const isAdding = (widgetId) => addingWidgetId.value === widgetId
const isAdded = (widgetId) => addedWidgetIds.value.has(widgetId)

const isRestricted = (widgetId) => {
  const ids = props.restrictedWidgetIds
  if (!ids) return false
  return ids instanceof Set ? ids.has(widgetId) : (Array.isArray(ids) && ids.includes(widgetId))
}

// Check if widget type is already on the dashboard in any section
const isWidgetOnDashboard = (widgetId) => {
  return props.existingWidgets.some(
    (w) => w.id === widgetId || w.id.startsWith(`${widgetId}-`)
  )
}

// Get tooltip text showing which section(s) the widget is added to
const getAddedTooltip = (widgetId) => {
  const matches = props.existingWidgets.filter(
    (w) => w.id === widgetId || w.id.startsWith(`${widgetId}-`)
  )
  const titles = [...new Set(matches.map((w) => w.sectionTitle).filter(Boolean))]
  return titles.length ? `Added to: ${titles.join(', ')}` : 'Already on dashboard'
}

function resetAddedOnHoverAway(widgetId) {
  if (addedWidgetIds.value.has(widgetId)) {
    addedWidgetIds.value = new Set([...addedWidgetIds.value].filter(id => id !== widgetId))
  }
}

const addedBadgeTooltipRef = ref(null)

function handleAddedBadgeMouseEnter(e, widgetId) {
  hoveredAddedBadgeEl.value = e.currentTarget
  hoveredAddedBadgeWidgetId.value = widgetId
}

function handleAddedBadgeMouseLeave() {
  hoveredAddedBadgeEl.value = null
  hoveredAddedBadgeWidgetId.value = null
}

const addedBadgeTooltipStyle = computed(() => {
  if (!hoveredAddedBadgeEl.value || !hoveredAddedBadgeWidgetId.value) return {}
  const rect = hoveredAddedBadgeEl.value.getBoundingClientRect()
  const gap = 8
  const padding = 12
  const tooltipHeight = addedBadgeTooltipRef.value?.offsetHeight ?? 36
  const tooltipWidth = addedBadgeTooltipRef.value?.offsetWidth ?? 120
  const vw = window.innerWidth
  const vh = window.innerHeight

  // Horizontal: clamp so tooltip stays in viewport (center at left with translateX(-50%))
  let left = rect.left + rect.width / 2
  const minLeft = padding + tooltipWidth / 2
  const maxLeft = vw - padding - tooltipWidth / 2
  left = Math.max(minLeft, Math.min(maxLeft, left))

  // Vertical: prefer above badge; if that would go off top, show below
  let top = rect.top - tooltipHeight - gap
  const showBelow = top < padding
  if (showBelow) {
    top = rect.bottom + gap
  }
  top = Math.max(padding, Math.min(vh - tooltipHeight - padding, top))

  return {
    left: `${left}px`,
    top: `${top}px`,
    transform: 'translateX(-50%)'
  }
})


const props = defineProps({
  /** When true, renders as full panel (no backdrop) for Optix parent panel */
  embedded: {
    type: Boolean,
    default: false
  },
  isOpen: {
    type: Boolean,
    default: false
  },
  /** Section to add the widget to when user clicks a preview. Null when panel was opened from the Add menu (then we ask). */
  targetSectionId: {
    type: String,
    default: null
  },
  sections: {
    type: Array,
    default: () => []
  },
  availableWidgets: {
    type: Array,
    required: true
  },
  restrictedWidgetIds: {
    type: [Set, Array],
    default: () => new Set()
  },
  existingWidgets: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:isOpen', 'add-widget'])

watch(() => props.isOpen, (open) => {
  if (open) {
    // Create observer after DOM renders
    nextTick(() => createObserver())
  } else {
    addingWidgetId.value = null
    addedWidgetIds.value = new Set()
    hoveredAddedBadgeEl.value = null
    hoveredAddedBadgeWidgetId.value = null
    destroyObserver()
  }
})

// Handle embedded mode where isOpen starts as true (watcher won't fire for initial value)
onMounted(() => {
  if (props.isOpen) {
    nextTick(() => createObserver())
  }
})

function sentenceCase(str) {
  if (!str || typeof str !== 'string') return str
  if (str === 'bookings & assignments') return 'Bookings & assignments'
  if (str === 'operational') return 'Inbox'
  if (str === 'operations') return 'Operations'
  if (str === 'users & community') return 'Plans'
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

// Section order for add widget panel; automations always last
const SECTION_ORDER = [
  'operational',
  'bookings & assignments',
  'operations',
  'revenue & financial',
  'growth',
  'users & community',
  'automations & efficiency'
]

const widgetsBySection = computed(() => {
  const groups = new Map()
  for (const section of SECTION_ORDER) {
    groups.set(section, [])
  }
  for (const widget of props.availableWidgets) {
    if (widget.hiddenFromAddPanel) continue
    const section = widget.section || 'operational'
    if (!groups.has(section)) groups.set(section, [])
    groups.get(section).push(widget)
  }
  return SECTION_ORDER
    .filter(section => groups.get(section)?.length)
    .map(section => ({ section, widgets: groups.get(section) }))
})

// When adding to a focused section, show previews in focused mode (name/KPI/date only, shorter)
const targetSectionIsFocused = computed(() => {
  if (!props.targetSectionId || !props.sections?.length) return false
  const section = props.sections.find(s => s.id === props.targetSectionId)
  return section?.displayMode === 'focused'
})

const close = () => {
  emit('update:isOpen', false)
}

const handleBackdropClick = (event) => {
  if (event.target === event.currentTarget) {
    close()
  }
}

function buildWidgetData(widgetTemplate) {
  return {
    id: widgetTemplate.id,
    component: markRaw(widgetTemplate.component),
    colSpan: widgetTemplate.defaultColSpan ?? 1,
    rowSpan: widgetTemplate.defaultRowSpan ?? 1,
    props: widgetTemplate.defaultProps || {}
  }
}

function addWidgetToSection(sectionId, widgetTemplate) {
  if (addingWidgetId.value || addedWidgetIds.value.has(widgetTemplate.id)) return
  addingWidgetId.value = widgetTemplate.id
  const widgetData = buildWidgetData(widgetTemplate)
  emit('add-widget', { sectionId, widget: widgetData })
  setTimeout(() => {
    addingWidgetId.value = null
    addedWidgetIds.value = new Set([...addedWidgetIds.value, widgetTemplate.id])
  }, 400)
}


// In preview, widgets use rowSpan to decide how many list rows to show; rowSpan 1 → 0 rows.
// When adding to focused section: show focused view (rowSpan 1 hides lists).
// Otherwise: override so tables/lists are visible in the add-widget panel.
const previewPropsForWidget = (widget) => {
  const base = { ...(widget.previewProps || {}) }
  if (targetSectionIsFocused.value) {
    return {
      ...base,
      rowSpan: 1,
      colSpan: Math.max(base.colSpan || 0, 2)
    }
  }
  return {
    ...base,
    rowSpan: Math.max(base.rowSpan || 0, 3),
    colSpan: Math.max(base.colSpan || 0, 2)
  }
}
</script>

<style scoped>
.add-widget-panel-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10002;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  pointer-events: none;
}

.add-widget-panel-container.embedded {
  position: absolute;
  pointer-events: auto;
  align-items: stretch;
  justify-content: stretch;
}

.add-widget-panel-container.embedded .add-widget-panel {
  width: 100%;
  max-width: none;
  border-radius: 0;
  border: none;
  box-shadow: none;
}

.add-widget-panel {
  background: unset;
  background-color: unset;
  width: 1000px;
  max-width: 95vw;
  height: 100%;
  border-radius: 16px 0 0 16px;
  border: 1px solid #e5e7eb;
  border-right: none;
  box-shadow: -4px 0 16px 6px rgba(0, 0, 0, 0.05), 1px 0 14px 0px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;
  transform: translateX(0);
  container-type: inline-size;
  container-name: add-widget-panel;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: none;
  flex-shrink: 0;
  background-color: #F4F5F7;
}

.panel-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: #111827;
  flex: 1;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.panel-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.widgets-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 24px;
  background-color: #F4F5F7;
  width: 100%;
}

.no-widgets {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.widget-section {
  margin-top: 24px;
  margin-bottom: 40px;
}

.widget-section:first-child {
  margin-top: 0;
}

.widget-section:last-child {
  margin-bottom: 0;
}

.widget-section-header {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  letter-spacing: -0.15px;
}

.widgets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 320px);
  grid-auto-rows: 310px;
  column-gap: 12px;
  row-gap: 16px;
  padding: 8px 0;
  align-items: stretch;
}

@container add-widget-panel (min-width: 920px) {
  .widgets-grid {
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 310px;
    column-gap: 8px;
    row-gap: 12px;
    align-items: stretch;
  }
}

.widgets-content.preview-mode-focused .widgets-grid {
  grid-auto-rows: 112px;
}

.widget-card {
  width: 320px;
  max-width: 320px;
  min-width: 0;
  justify-self: start;
  border: none;
  border-radius: 0;
  padding: 0;
  transition: all 0.2s;
  background: transparent;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: visible; /* let section dropdown extend outside */
}

@container add-widget-panel (min-width: 920px) {
  .widget-card {
    width: 100%;
    max-width: 100%;
    justify-self: stretch;
  }
}

.widget-card:hover {
  transform: none;
}

.widget-card:active {
  transform: none;
}

/* Preview overlay: Add button centered */
.widget-preview-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background: rgba(0, 0, 0, 0.06);
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
  border-radius: 12px;
}

.widget-card:hover .widget-preview-overlay {
  opacity: 1;
  pointer-events: auto;
}

.widget-add-actions {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.widget-add-actions-centered {
  margin: 0 auto;
}

.widget-add-btn {
  min-width: 100px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #2383e2;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.widget-add-btn:hover {
  background: #1d6bc4;
}

.widget-add-btn:disabled {
  cursor: default;
}

.widget-add-btn.is-added {
  background: #2383e2;
}

.widget-add-btn.is-added:hover {
  background: #2383e2;
}

.add-btn-loading {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.add-btn-loading .dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
  animation: dot-pulse 0.6s ease-in-out infinite both;
}

.add-btn-loading .dot:nth-child(2) {
  animation-delay: 0.1s;
}

.add-btn-loading .dot:nth-child(3) {
  animation-delay: 0.2s;
}

@keyframes dot-pulse {
  0%, 80%, 100% {
    opacity: 0.4;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

.widget-preview-overlay-disabled {
  pointer-events: none;
}

.widget-add-disabled-message {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

/* Blurred preview for role-restricted widgets */
.preview-container--blurred {
  filter: blur(6px);
  pointer-events: none;
}

/* Fake content placeholder under blur */
.widget-fake-preview {
  width: 100%;
  height: 100%;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f8f9fa;
}

.widget-fake-title {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

.widget-fake-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.widget-fake-bar {
  height: 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  width: 100%;
}

.widget-fake-bar--short {
  width: 60%;
}

.widget-fake-bar--medium {
  width: 80%;
}

/* Role-restricted tooltip (black style) */
.widget-add-actions--tooltip-trigger {
  position: relative;
}

.role-restricted-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  font-size: 12px;
  white-space: nowrap;
  border-radius: 4px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  z-index: 10;
}

.widget-add-actions--tooltip-trigger:hover .role-restricted-tooltip {
  opacity: 1;
}

.widget-add-btn.is-restricted {
  opacity: 0.7;
  cursor: not-allowed;
}

.widget-on-dashboard-badge {
  position: absolute;
  top: 16px;
  right: 12px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.widget-on-dashboard-badge-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  flex-shrink: 0;
  background-color: #DBEDDB;
  color: #1C3829;
}

/* Black tooltip - teleported to body to escape overflow: hidden */
.widget-on-dashboard-tooltip--teleported {
  position: fixed;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  font-size: 12px;
  white-space: nowrap;
  border-radius: 4px;
  pointer-events: none;
  z-index: 10010;
}

.widget-preview {
  margin-bottom: 0;
  border-radius: 12px;
  overflow: hidden;
  background: white;
  border-width: 0;
  border-style: none;
  border-color: rgba(0, 0, 0, 0);
  border-image: none;
  height: 310px;
  min-height: 310px;
  max-height: 310px;
  width: 320px;
  position: relative;
  flex-shrink: 0;
}

.widget-preview.preview-large {
  height: 310px;
  width: 320px;
}

.widget-preview.preview-focused {
  height: 112px;
  min-height: 112px;
  max-height: 112px;
  width: 320px;
}

@container add-widget-panel (min-width: 920px) {
  .widget-preview,
  .widget-preview.preview-large {
    width: 100%;
    min-width: 0;
    height: 310px;
    min-height: 310px;
    max-height: 310px;
  }

  .widget-preview.preview-focused {
    width: 100%;
    min-width: 0;
    height: 112px;
    min-height: 112px;
    max-height: 112px;
  }

  /* Focused preview has no scaling - keep KPI at 28px */
  .preview-focused .preview-wrapper :deep(.metric-value .value),
  .preview-focused .preview-wrapper :deep(.metric-value) {
    font-size: 28px !important;
  }

  .widget-card .preview-wrapper {
    width: 100%;
    height: 100%;
    flex-shrink: 1;
    min-width: 0;
  }

  .widget-card .preview-wrapper :deep(.base-widget) {
    transform: scale(0.9) !important;
    transform-origin: top center !important;
  }

  .widget-card .preview-wrapper :deep(.widget-title) {
    font-size: calc(15px / 0.9) !important;
  }

  /* Use smaller KPI size (28px unscaled) so all previews match - no scale compensation */
  .widget-card .preview-wrapper :deep(.metric-value .value),
  .widget-card .preview-wrapper :deep(.metric-value) {
    font-size: 28px !important;
  }
}


/* Wrapper 310px so no scroll; content scaled to fit */
.preview-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
  border-radius: 12px;
}

.preview-wrapper {
  width: 320px;
  height: 310px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
}

/* Focused preview: shorter wrapper, no scaling */
.preview-focused .preview-container,
.preview-focused .preview-wrapper {
  height: 112px !important;
}

.preview-focused .preview-wrapper :deep(.base-widget) {
  transform: none !important;
  width: 100% !important;
  height: auto !important;
  min-height: 0 !important;
  flex-shrink: 0 !important;
}

/* In focused preview there's no scaling, so use 28px directly (don't apply scale compensation) */
.preview-focused .preview-wrapper :deep(.metric-value .value),
.preview-focused .preview-wrapper :deep(.metric-value) {
  font-size: 28px !important;
}

/* Center the BaseWidget content in the preview (fixes right-shift from overflow) */
.preview-wrapper :deep(.base-widget-wrapper) {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: flex-start !important;
}

/* Hide the BaseWidget border when inside preview and reduce padding */
.preview-wrapper :deep(.base-widget) {
  border: none !important;
  box-shadow: none !important;
  flex-shrink: 0;
  padding: 10px 12px 12px 12px !important;
}

.preview-wrapper :deep(.base-widget .widget-header) {
  margin-bottom: 12px !important;
}

/* Scale applied to base-widget so wrapper stays 260px - no scroll/whitespace */
.widget-card .preview-wrapper {
  transform: none;
}

.widget-card .preview-wrapper :deep(.base-widget) {
  width: calc(320px / 0.9) !important;
  height: calc(310px / 0.9) !important;
  min-height: calc(310px / 0.9) !important;
  display: flex !important;
  flex-direction: column !important;
  transform: scale(0.9) !important;
  transform-origin: top center !important;
  flex-shrink: 0 !important;
}

/* Match BaseWidget title: 15px, font-weight 500, color rgba(0,0,0,0.87) */
.widget-card .preview-wrapper :deep(.widget-title) {
  font-size: calc(15px / 0.9) !important;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.87) !important;
  line-height: 1.15 !important;
}

/* Use smaller KPI size (28px unscaled) so all previews match - no scale compensation */
.widget-card .preview-wrapper :deep(.metric-value .value),
.widget-card .preview-wrapper :deep(.metric-value) {
  font-size: 28px !important;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.87) !important;
  line-height: 1.15 !important;
}

/* Force widget content to take remaining height so list/table is visible */
.preview-wrapper :deep(.base-widget .widget-content) {
  width: 100% !important;
  flex: 1 1 0 !important;
  min-height: 0 !important;
  overflow: visible !important;
  display: flex !important;
  flex-direction: column !important;
}

.preview-wrapper :deep(.widget-content-wrapper) {
  width: 100% !important;
  flex: 1 1 0 !important;
  min-height: 0 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

.preview-wrapper :deep(.bookings-list),
.preview-wrapper :deep(.tours-list),
.preview-wrapper :deep(.plans-list),
.preview-wrapper :deep(.assignments-list) {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  overflow: visible !important;
  visibility: visible !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Disable interactivity in preview widgets */
.preview-wrapper :deep(.base-widget),
.preview-wrapper :deep(.base-widget *) {
  pointer-events: none !important;
  cursor: default !important;
}

/* Remove hover effects on list items in preview */
.preview-wrapper :deep(.booking-item:hover),
.preview-wrapper :deep(.plan-item:hover),
.preview-wrapper :deep(.assignment-item:hover),
.preview-wrapper :deep(.tour-item:hover) {
  background-color: transparent !important;
  border-radius: 0 !important;
  margin: 0 -4px !important;
  padding: 0 4px !important;
}

/* Remove clickable cursor on items */
.preview-wrapper :deep(.booking-item),
.preview-wrapper :deep(.plan-item),
.preview-wrapper :deep(.assignment-item),
.preview-wrapper :deep(.tour-item) {
  cursor: default !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* Add padding to widgets in preview */
.widget-card .preview-wrapper :deep(.base-widget) {
  padding: 16px 20px 20px 20px !important;
}

.widget-card .preview-wrapper :deep(.enrollments-table),
.widget-card .preview-wrapper :deep(table) {
  margin-top: 8px;
  padding: 0 4px;
}

/* Reduce spacing on count sections in preview */
.preview-wrapper :deep(.plan-count),
.preview-wrapper :deep(.assignment-count),
.preview-wrapper :deep(.booking-count),
.preview-wrapper :deep(.tour-count) {
  margin-bottom: 2px !important;
}

.preview-wrapper :deep(.count-label) {
  margin-top: 2px !important;
  padding-bottom: 2px !important;
}

/* Reduce widget content wrapper spacing */
.preview-wrapper :deep(.widget-content-wrapper) {
  gap: 0 !important;
}

/* Reduce padding and gaps on list items in preview */
.preview-wrapper :deep(.plans-list),
.preview-wrapper :deep(.assignments-list),
.preview-wrapper :deep(.bookings-list),
.preview-wrapper :deep(.tours-list) {
  gap: 4px !important;
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.preview-wrapper :deep(.plan-item),
.preview-wrapper :deep(.assignment-item),
.preview-wrapper :deep(.booking-item),
.preview-wrapper :deep(.tour-item) {
  gap: 4px !important;
  padding: 0 !important;
  margin: 0 !important;
}

.preview-wrapper :deep(.plan-row),
.preview-wrapper :deep(.assignment-row),
.preview-wrapper :deep(.booking-row),
.preview-wrapper :deep(.tour-row) {
  gap: 6px !important;
}

.widget-title {
  margin: 0 0 4px 0;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  text-align: center;
  line-height: 1.3;
}

.widget-description {
  margin: 0;
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
  text-align: center;
  padding: 0 4px;
}

/* Side panel animation */
.side-panel-enter-active,
.side-panel-leave-active {
  transition: opacity 0.3s ease;
}

.side-panel-enter-active .add-widget-panel,
.side-panel-leave-active .add-widget-panel {
  transition: transform 0.3s ease;
}

.side-panel-enter-from .add-widget-panel,
.side-panel-leave-to .add-widget-panel {
  transform: translateX(100%);
}

.side-panel-enter-from,
.side-panel-leave-to {
  opacity: 0;
}
</style>
